import { useEffect } from 'react';

const clamp01 = (value) => Math.min(1, Math.max(0, value));

// Moves a pool of lantern light over a painting by setting --lantern-x and
// --lantern-y (percentages of the element) on it. With a mouse, the light
// follows the pointer and drifts back to rest when it leaves. On touch
// screens it sweeps across the painting as the page scrolls past it.
export default function useLantern(ref, enabled = true) {
  useEffect(() => {
    const node = ref.current;
    if (!enabled || !node || typeof window === 'undefined' || !window.matchMedia) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let frame = 0;
    const place = (x, y) => {
      node.style.setProperty('--lantern-x', `${(clamp01(x) * 100).toFixed(2)}%`);
      node.style.setProperty('--lantern-y', `${(clamp01(y) * 100).toFixed(2)}%`);
    };

    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      let target = null;
      const onMove = (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const rect = node.getBoundingClientRect();
        target = [(event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height];
        node.classList.add('is-lantern-held');
        if (!frame) {
          frame = window.requestAnimationFrame(() => {
            frame = 0;
            if (target) place(target[0], target[1]);
          });
        }
      };
      const onLeave = () => {
        target = null;
        node.classList.remove('is-lantern-held');
        node.style.removeProperty('--lantern-x');
        node.style.removeProperty('--lantern-y');
      };
      node.addEventListener('pointermove', onMove);
      node.addEventListener('pointerleave', onLeave);
      return () => {
        if (frame) window.cancelAnimationFrame(frame);
        node.removeEventListener('pointermove', onMove);
        node.removeEventListener('pointerleave', onLeave);
      };
    }

    let onScreen = false;
    const sweep = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const viewport = window.innerHeight || 1;
      // 0 as the painting enters at the bottom, 1 as it leaves at the top.
      const t = clamp01((viewport - rect.top) / (viewport + rect.height));
      place(0.16 + 0.68 * t, 0.7 - 0.24 * Math.sin(t * Math.PI));
    };
    const onScroll = () => {
      if (onScreen && !frame) frame = window.requestAnimationFrame(sweep);
    };
    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(([entry]) => {
            onScreen = Boolean(entry && entry.isIntersecting);
            onScroll();
          })
        : null;
    if (observer) observer.observe(node);
    else onScreen = true;
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, [ref, enabled]);
}
